import { createRouter, createWebHistory } from 'vue-router'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import SILayout from '@/layouts/SILayout.vue'
import { useAuth } from '@simplifi/auth'
import { registerGuard } from './router-guard'
import {
  hasConfigAccess,
  hasTranscriptionAccess,
  hasSetContinuityStateAccess,
  hasMessagingAccess,
  hasMessagingAdminAccess,
} from '@/lib/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: DefaultLayout,
      meta: {
        requiresAuth: true,
        title: 'Home Emergency',
      },

      children: [
        {
          path: '',
          name: 'home',
          meta: { title: 'HE - Workstreams' },
          component: () => import('@/views/HomeView.vue'),
        },
        {
          path: 'auditing',
          name: 'auditing',
          meta: { title: 'Auditing' },
          component: () => import('@/views/AuditingView.vue'),
        },
        {
          path: 'he/complaints',
          name: 'he/complaints',
          meta: { title: 'HE - Complaints' },
          component: () => import('@/views/ComplaintsView.vue'),
        },
        {
          path: 'he/call-summarisation',
          name: 'he/callSummarisation',
          meta: { title: 'Call Summarisation' },
          component: () => import('@/views/CallSummarisationView.vue'),
          beforeEnter: () => {
            if (!hasTranscriptionAccess()) {
              return { name: 'permissionError' }
            }
          },
          children: [
            {
              path: ':jobId',
              name: 'he/callSummarisationJob',
              component: () => import('@/views/CallSummarisationJobView.vue'),
            },
          ],
        },
        {
          path: 'config',
          name: 'config',
          meta: { title: 'Config' },
          component: () => import('@/layouts/ConfigLayout.vue'),
          beforeEnter: () => {
            if (!hasConfigAccess()) {
              return { name: 'permissionError' }
            }
          },
          children: [
            {
              path: '',
              name: 'timers',
              component: () => import('@/views/Config/TimersView.vue'),
              beforeEnter: () => {
                if (
                  !useAuth().hasPermission(
                    'Operations.ConfigureWorkstreamTimers' as never,
                  )
                ) {
                  return { name: 'permissionError' }
                }
              },
            },
            {
              path: 'short-codes',
              name: 'shortCodes',
              component: () => import('@/views/Config/ShortCodesView.vue'),
              beforeEnter: () => {
                if (
                  !useAuth().hasPermission(
                    'Operations.ConfigureShortCodes' as never,
                  )
                ) {
                  return { name: 'permissionError' }
                }
              },
            },
            {
              path: 'trade-categories',
              name: 'tradeCategories',
              component: () => import('@/views/Config/TradeCategoriesView.vue'),
              beforeEnter: () => {
                if (
                  !useAuth().hasPermission(
                    'Operations.ConfigureTradeCategories' as never,
                  )
                ) {
                  return { name: 'permissionError' }
                }
              },
            },
            {
              path: 'data',
              name: 'data',
              component: () => import('@/views/Config/DataView.vue'),
            },
            {
              path: 'message-templates',
              name: 'messageTemplates',
              component: () =>
                import('@/views/Config/MessageTemplatesView.vue'),
              beforeEnter: () => {
                if (
                  !useAuth().hasPermission(
                    'Operations.TemplateAdministrator' as never,
                  )
                ) {
                  return { name: 'permissionError' }
                }
              },
            },
            {
              path: 'email-signatures',
              name: 'emailSignatures',
              component: () => import('@/views/Config/EmailSignaturesView.vue'),
              beforeEnter: () => {
                if (
                  !useAuth().hasPermission(
                    'Operations.CommunicationAdministrator' as never,
                  )
                ) {
                  return { name: 'permissionError' }
                }
              },
            },
          ],
        },
        {
          path: 'continuity-service-config',
          name: 'continuityServiceConfig',
          meta: { title: 'Continuity Service Config' },
          component: () => import('@/views/ContinuityConfigView.vue'),
          beforeEnter: () => {
            if (!hasSetContinuityStateAccess()) {
              return { name: 'permissionError' }
            }
          },
        },
        {
          path: 'control-room',
          name: 'controlRoom',
          meta: { title: 'Control Room' },
          component: () => import('@/views/ControlRoomView.vue'),
          beforeEnter: () => {
            if (
              !useAuth().hasPermission(
                'Operations.ControlRoomStatistics' as never,
              )
            ) {
              return { name: 'permissionError' }
            }
          },
        },
        {
          path: 'stats',
          name: 'stats',
          meta: { title: 'Stats' },
          component: () => import('@/views/StatsView.vue'),
          beforeEnter: () => {
            if (
              !useAuth().hasPermission(
                'Operations.WorkstreamStatistics' as never,
              )
            ) {
              return { name: 'permissionError' }
            }
          },
        },
        {
          path: 'examples',
          name: 'examples',
          meta: { title: 'Examples' },
          children: [
            {
              path: 'charts',
              name: 'charts',
              meta: { title: 'Charts Example' },
              component: () => import('@/views/ChartsExampleView.vue'),
            },
          ],
        },
      ],
    },

    {
      path: '/si',
      component: SILayout,
      meta: {
        requiresAuth: true,
        title: 'Site Investigation',
      },

      children: [
        {
          path: '',
          name: 'si',
          meta: { title: 'SI - Workstreams' },
          component: () => import('@/views/SIView.vue'),
        },
      ],
    },

    {
      path: '/messaging',
      component: DefaultLayout,
      meta: {
        requiresAuth: true,
        title: 'MERCURi',
      },
      beforeEnter: () => {
        if (!hasMessagingAccess()) {
          return { name: 'permissionError' }
        }
      },
      children: [
        {
          path: '',
          component: () => import('@/layouts/MessagingLayout.vue'),
          children: [
            {
              path: '',
              name: 'messaging',
              meta: { title: 'MERCURi' },
              component: () => import('@/views/Messaging/ChannelView.vue'),
            },
            {
              path: 'assigned',
              name: 'messagingAssigned',
              meta: { title: 'Assigned to Me' },
              component: () => import('@/views/Messaging/AssignedView.vue'),
            },
            {
              path: 'channel/:channelId',
              name: 'channel',
              meta: { title: 'Channel' },
              component: () => import('@/views/Messaging/ChannelView.vue'),
            },
            {
              path: 'channel/:channelId/message/:messageId',
              name: 'message',
              meta: { title: 'Message' },
              component: () =>
                import('@/views/Messaging/MessageWorkspaceView.vue'),
            },
            {
              path: 'statistics',
              name: 'communicationStatistics',
              meta: { title: 'Communication Statistics' },
              component: () =>
                import('@/views/Messaging/CommunicationStatisticsView.vue'),
            },
            {
              path: 'config/channels',
              name: 'messagingChannelConfig',
              meta: { title: 'Channel Config' },
              component: () =>
                import('@/views/Messaging/Config/ChannelConfigView.vue'),
              beforeEnter: () => {
                if (!hasMessagingAdminAccess()) {
                  return { name: 'permissionError' }
                }
              },
            },
            {
              path: 'config/users',
              name: 'messagingUserConfig',
              meta: { title: 'User Config' },
              component: () =>
                import('@/views/Messaging/Config/UserConfigView.vue'),
              beforeEnter: () => {
                if (!hasMessagingAdminAccess()) {
                  return { name: 'permissionError' }
                }
              },
            },
          ],
        },
      ],
    },

    {
      path: '/identity/callback',
      component: AuthLayout,
      meta: {
        requiresAuth: true,
      },

      children: [
        {
          path: '',
          name: 'identityCallback',
          component: () =>
            import('@/views/authentication/IdentityCallback.vue'),
        },
      ],
    },

    {
      path: '/signin/error',
      name: 'signinError',
      meta: { title: 'Error' },
      component: () => import('@/views/authentication/SigninError.vue'),
    },
    {
      path: '/permission/error',
      name: 'permissionError',
      meta: { title: 'Error' },
      component: () => import('@/views/authentication/PermissionError.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'notFound',
      meta: { title: '404 - Not Found' },
      component: () => import('@/views/Errors/Error404.vue'),
    },
  ],
})

registerGuard(router)

router.beforeEach((to, from, next) => {
  document.title = [to.meta.title, `${import.meta.env.VITE_APP_TITLE}`]
    .filter((t) => !!t)
    .join(' | ')

  next()
})

export default router
